import React from "react";
import { useNavigate } from "react-router-dom";
import { Workflow } from "../../services/WorkflowService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MoreHorizontalIcon,
  WorkflowIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import moment from "moment";

interface WorkflowWithApp extends Workflow {
  application_name?: string;
}

interface WorkflowsTableProps {
  workflows: WorkflowWithApp[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  onDelete: (workflowId: number) => void;
  onCreate: () => void;
  deleteDialogOpen: number | null;
  setDeleteDialogOpen: (id: number | null) => void;
  pageSize: number;
  totalWorkflows: number;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "—";
  return moment(dateStr).format("MMM D, YYYY");
};

const formatTime = (dateStr?: string) => {
  if (!dateStr) return "";
  return moment(dateStr).format("h:mm A");
};

const WorkflowsTable: React.FC<WorkflowsTableProps> = ({
  workflows,
  isLoading,
  currentPage,
  setCurrentPage,
  onDelete,
  onCreate,
  deleteDialogOpen,
  setDeleteDialogOpen,
  pageSize,
  totalWorkflows,
}) => {
  const navigate = useNavigate();
  const totalPages = Math.ceil(totalWorkflows / pageSize);

  if (isLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3 px-6">Name</TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">Ref Name</TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">Application</TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">Description</TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">Created</TableHead>
            <TableHead className="py-3 w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i} className="hover:bg-transparent">
              <TableCell className="py-4 px-6">
                <div className="h-4 bg-muted rounded w-32 animate-pulse" />
              </TableCell>
              <TableCell className="py-4">
                <div className="h-5 bg-muted rounded w-28 animate-pulse" />
              </TableCell>
              <TableCell className="py-4">
                <div className="h-4 bg-muted rounded w-24 animate-pulse" />
              </TableCell>
              <TableCell className="py-4">
                <div className="h-4 bg-muted rounded w-48 animate-pulse" />
              </TableCell>
              <TableCell className="py-4">
                <div className="space-y-1.5">
                  <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                  <div className="h-3 bg-muted rounded w-16 animate-pulse" />
                </div>
              </TableCell>
              <TableCell className="py-4">
                <div className="h-8 w-8 bg-muted rounded animate-pulse" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (workflows.length === 0 && totalWorkflows === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8">
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <WorkflowIcon className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-gc-med text-foreground mb-2">
          No workflows yet
        </h3>
        <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
          Create your first workflow to define automated pipelines that can
          include agents, tools, and custom logic.
        </p>
        <Button onClick={onCreate} className="gap-2">
          <PlusIcon className="w-4 h-4" />
          Create Workflow
        </Button>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3 px-6">
              Name
            </TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">
              Ref Name
            </TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">
              Application
            </TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">
              Description
            </TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3">
              Created
            </TableHead>
            <TableHead className="font-gc-med text-xs uppercase tracking-wider text-muted-foreground py-3 w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {workflows.map((workflow) => (
            <TableRow
              key={workflow.id}
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => navigate(`/forge/workflows/${workflow.id}`)}
            >
              <TableCell className="py-4 px-6">
                <div className="font-gc-med text-foreground">
                  {workflow.workflow_name}
                </div>
              </TableCell>
              <TableCell className="py-4">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                  {workflow.workflow_ref_name}
                </code>
              </TableCell>
              <TableCell className="py-4">
                <div className="text-sm text-muted-foreground">
                  {workflow.application_name || "None"}
                </div>
              </TableCell>
              <TableCell className="py-4">
                <div className="text-sm text-muted-foreground truncate max-w-[300px]">
                  {workflow.description || "No description"}
                </div>
              </TableCell>
              <TableCell className="py-4">
                <div className="space-y-1">
                  <div className="text-sm font-gc-reg text-foreground">
                    {formatDate(workflow.date_created)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatTime(workflow.date_created)}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontalIcon className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteDialogOpen(workflow.id);
                      }}
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <AlertDialog
                  open={deleteDialogOpen === workflow.id}
                  onOpenChange={(open) =>
                    setDeleteDialogOpen(open ? workflow.id : null)
                  }
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-gc-med">
                        Delete Workflow
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete &quot;
                        {workflow.workflow_name}&quot;? This action cannot be
                        undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(workflow.id);
                        }}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, totalWorkflows)} of{" "}
            {totalWorkflows} workflows
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="gap-1"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                const isCurrentPage = pageNum === currentPage;
                return (
                  <Button
                    key={pageNum}
                    variant={isCurrentPage ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={cn(
                      "w-8 h-8 p-0",
                      isCurrentPage &&
                        "bg-maitai-green text-white hover:bg-maitai-green/90"
                    )}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="gap-1"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkflowsTable;
